import React, { useState } from "react";
import { Link2, Check } from "lucide-react";

/**
 * ShareBar — small row of platform-specific share buttons for an archive entry.
 * Each button opens the canonical share URL in a new tab. Copy uses the
 * clipboard API with a falling-back affordance.
 */
export const ShareBar = ({ url, title, description }) => {
  const [copied, setCopied] = useState(false);
  const safeUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const safeTitle = title || "Will Not Be Forgotten";
  const safeText = description || "";

  const targets = [
    {
      id: "x",
      label: "Share on X",
      brand: "X",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${safeTitle} — ${safeText}`.trim())}&url=${encodeURIComponent(safeUrl)}`,
      Icon: XIcon,
    },
    {
      id: "facebook",
      label: "Share on Facebook",
      brand: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(safeUrl)}`,
      Icon: FacebookIcon,
    },
    {
      id: "linkedin",
      label: "Share on LinkedIn",
      brand: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(safeUrl)}`,
      Icon: LinkedInIcon,
    },
    {
      id: "bluesky",
      label: "Share on Bluesky",
      brand: "Bluesky",
      href: `https://bsky.app/intent/compose?text=${encodeURIComponent(`${safeTitle} — ${safeUrl}`)}`,
      Icon: BlueskyIcon,
    },
    {
      id: "email",
      label: "Share by email",
      brand: "Email",
      href: `mailto:?subject=${encodeURIComponent(safeTitle)}&body=${encodeURIComponent(`${safeText}\n\n${safeUrl}`)}`,
      Icon: EmailIcon,
    },
  ];

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(safeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — silent */
    }
  };

  return (
    <div
      data-testid="share-bar"
      role="group"
      aria-label="Share this entry"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span
        aria-hidden
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "hsl(var(--muted))",
          marginRight: 6,
        }}
      >
        Share
      </span>
      {targets.map(({ id, label, href, Icon }) => (
        <a
          key={id}
          href={href}
          target={id === "email" ? undefined : "_blank"}
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          data-testid={`share-${id}`}
          style={btnStyle}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "hsl(var(--accent))"; e.currentTarget.style.color = "hsl(var(--accent))"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "hsl(var(--line))"; e.currentTarget.style.color = "hsl(var(--ink-soft))"; }}
        >
          <Icon size={15} />
        </a>
      ))}
      <button
        type="button"
        onClick={onCopy}
        aria-label={copied ? "Link copied" : "Copy link"}
        title={copied ? "Link copied" : "Copy link"}
        data-testid="share-copy"
        style={{
          ...btnStyle,
          background: copied ? "hsl(var(--accent))" : "hsl(var(--bg-elev))",
          color: copied ? "hsl(var(--accent-ink))" : "hsl(var(--ink-soft))",
          borderColor: copied ? "hsl(var(--accent))" : "hsl(var(--line))",
          cursor: "pointer",
          fontFamily: "var(--font-body)",
          gap: 6,
          paddingLeft: copied ? 12 : 9,
          paddingRight: copied ? 12 : 9,
        }}
        onMouseEnter={(e) => {
          if (copied) return;
          e.currentTarget.style.borderColor = "hsl(var(--accent))";
          e.currentTarget.style.color = "hsl(var(--accent))";
        }}
        onMouseLeave={(e) => {
          if (copied) return;
          e.currentTarget.style.borderColor = "hsl(var(--line))";
          e.currentTarget.style.color = "hsl(var(--ink-soft))";
        }}
      >
        {copied ? <Check size={14} aria-hidden /> : <Link2 size={15} aria-hidden />}
        {copied && (
          <span style={{ fontSize: "0.74rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Copied</span>
        )}
      </button>
    </div>
  );
};

const btnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 34,
  height: 34,
  border: "1px solid hsl(var(--line))",
  background: "hsl(var(--bg-elev))",
  color: "hsl(var(--ink-soft))",
  borderRadius: 999,
  textDecoration: "none",
  transition: "border-color 160ms ease, color 160ms ease, background-color 160ms ease",
};

// --- minimal brand glyphs (inline SVG so we don't pull a brand-icon lib) ---
const XIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
    <path d="M18.244 2H21l-6.52 7.452L22 22h-6.828l-4.78-6.39L4.8 22H2.04l6.97-7.96L2 2h6.91l4.34 5.83L18.244 2Zm-2.395 18.4h1.86L7.27 3.5H5.27l10.58 16.9Z" />
  </svg>
);

const FacebookIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
    <path d="M13.5 21.95v-7.86h2.64l.4-3.07H13.5V9.06c0-.89.25-1.5 1.52-1.5h1.62V4.81a21.74 21.74 0 0 0-2.36-.12c-2.34 0-3.94 1.42-3.94 4.04v2.28H7.7v3.07h2.64v7.86Z" />
  </svg>
);

const LinkedInIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
    <path d="M4.98 3.5C4.98 4.88 3.87 6 2.49 6 1.11 6 0 4.88 0 3.5 0 2.12 1.12 1 2.5 1c1.38 0 2.48 1.12 2.48 2.5ZM.22 8h4.55v14H.22V8Zm7.7 0h4.36v1.92h.06c.6-1.13 2.07-2.32 4.27-2.32 4.57 0 5.42 3 5.42 6.91V22h-4.55v-6.66c0-1.59-.03-3.63-2.21-3.63-2.21 0-2.55 1.73-2.55 3.51V22H7.92V8Z" />
  </svg>
);

const BlueskyIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden focusable="false">
    <path d="M5.83 3.5c2.74 2.07 5.7 6.27 6.79 8.52.02.05.06.05.08 0C13.79 9.77 16.75 5.57 19.5 3.5c1.97-1.49 5.17-2.64 5.17 1.02 0 .73-.42 6.13-.66 7.01-.86 3.04-3.95 3.81-6.71 3.34 4.83.82 6.06 3.55 3.4 6.27-5.04 5.18-7.24-1.3-7.81-2.96-.1-.31-.15-.45-.15-.32 0-.13-.05.01-.15.32-.57 1.66-2.77 8.14-7.81 2.96-2.65-2.72-1.42-5.45 3.4-6.27-2.76.47-5.85-.3-6.71-3.34-.24-.88-.66-6.28-.66-7.01 0-3.66 3.2-2.51 5.17-1.02h.84Z" />
  </svg>
);

const EmailIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m4 7 8 6 8-6" />
  </svg>
);
